import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { FiscalBanner } from '@/components/FiscalBanner';
import { FiscalCountdown } from '@/components/FiscalCountdown';
import { FiscalSimulator } from '@/components/FiscalSimulator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChefHat, ArrowRight, Check, X, Star, Phone, 
  AlertTriangle, TrendingDown, Calculator, Flame
} from 'lucide-react';

// Images cuisine moderne
const HERO_IMAGE = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1600&auto=format&fit=crop";
const CONTENT_IMAGE = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop";

export const RenovationCuisinePage = () => {
  const formRef = useRef(null);
  const simulatorRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSimulator = () => {
    simulatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const questions = [
    { id: 'type_cuisine', label: 'Type de projet', type: 'select', options: ['Rénovation complète', 'Remplacement meubles', 'Plan de travail', 'Électroménager', 'Autre'] },
    { id: 'surface', label: 'Surface cuisine', type: 'text', placeholder: 'Ex: 12 m²' },
    { id: 'localisation', label: 'Localisation', type: 'text', placeholder: 'Ex: Lausanne, Genève...' },
    { id: 'budget', label: 'Budget estimatif', type: 'text', placeholder: 'Ex: 35\'000 CHF' },
    { id: 'optimisation_fiscale', label: 'Optimisation fiscale ?', type: 'select', options: ['Oui, c\'est prioritaire', 'J\'aimerais en savoir plus', 'Non'] }
  ];

  // Exemples spécifiques cuisine
  const cuisineExamples = [
    { type: 'Cuisine IKEA posée', budget: 15000, description: 'Meubles + pose + électroménager basique' },
    { type: 'Cuisine sur mesure', budget: 35000, description: 'Design personnalisé + électroménager premium' },
    { type: 'Cuisine haut de gamme', budget: 60000, description: 'Matériaux nobles + électroménager intégré' }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Rénovation de cuisine en Suisse romande",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Homely Solutions"
    },
    "description": "Rénovation de cuisine avec optimisation fiscale avant la réforme 2028"
  };

  return (
    <div className="min-h-screen bg-background" data-testid="renovation-cuisine-page">
      <SEOHead 
        title="Rénovation Cuisine Suisse | Déductions fiscales avant 2028 | Homely Solutions"
        description="Rénovez votre cuisine en profitant des déductions fiscales actuelles. Suite à la réforme votée, ces avantages disparaîtront. Agissez maintenant."
        keywords="rénovation cuisine suisse, cuisine sur mesure, déduction fiscale cuisine, rénovation cuisine lausanne genève"
        jsonLd={jsonLd}
      />

      {/* Bannière fiscale urgente */}
      <FiscalBanner onSimulatorClick={scrollToSimulator} />
      
      <Header />
      
      {/* ============================================
          HERO - ANGLE FISCAL CUISINE
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] md:min-h-[85vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Cuisine moderne rénovée" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <ChefHat className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-orange-500 text-white border-orange-400 text-xs sm:text-sm">
                Fenêtre fiscale limitée
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Votre nouvelle cuisine vous coûte moins cher
              <span className="block text-orange-300 mt-2 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">si vous la faites maintenant.</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-3 sm:mb-4 max-w-2xl">
              Une cuisine à 35'000 CHF peut vous revenir à <strong>26'250 CHF</strong> grâce aux déductions fiscales actuelles.
            </p>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white font-semibold mb-5 sm:mb-8 max-w-2xl">
              Après la réforme ? Vous paierez <span className="text-orange-300">35'000 CHF</span>. Point.
            </p>

            {/* Compte à rebours */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 mb-5 sm:mb-8 inline-block">
              <FiscalCountdown />
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
                onClick={scrollToSimulator}
                data-testid="cuisine-cta-simulator"
              >
                <Calculator className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Simuler mon économie
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] backdrop-blur-sm w-full sm:w-auto"
                onClick={scrollToForm}
              >
                <TrendingDown className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Demander un devis
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION PROBLÈME - SPÉCIFIQUE CUISINE
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="mb-3 sm:mb-4 bg-red-100 text-red-700 text-xs sm:text-sm">Le constat</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 px-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Votre cuisine vieillit. Votre avantage fiscal aussi.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Chaque jour qui passe, c'est un jour de moins pour profiter des déductions fiscales sur votre projet cuisine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-green-800 mb-6 flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                Rénover maintenant
              </h3>
              <ul className="space-y-4">
                {[
                  'Déduction fiscale sur les travaux',
                  'Coût réel réduit de ~25%',
                  'Cuisine neuve + économie d\'impôts'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-green-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-red-800 mb-6 flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                Attendre la réforme
              </h3>
              <ul className="space-y-4">
                {[
                  'Plus aucune déduction possible',
                  'Même cuisine, prix plein',
                  'Opportunité perdue définitivement'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X className="h-6 w-6 text-red-600 flex-shrink-0" />
                    <span className="text-red-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12 p-6 bg-orange-100 rounded-xl max-w-2xl mx-auto">
            <Flame className="h-8 w-8 text-orange-600 mx-auto mb-3" />
            <p className="text-xl font-bold text-orange-800">
              Une cuisine à 35'000 CHF = <span className="line-through">35'000</span> → <span className="text-green-700">26'250 CHF</span> aujourd'hui
            </p>
            <p className="text-orange-700 mt-2">
              Économie potentielle : <strong>8'750 CHF</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          SIMULATEUR FISCAL
          ============================================ */}
      <section className="py-20 bg-gray-50" ref={simulatorRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-700">Simulation cuisine</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Combien coûte réellement votre cuisine ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Entrez le budget de votre projet cuisine et découvrez immédiatement 
                la différence entre agir maintenant et attendre.
              </p>

              <FiscalCountdown />
            </div>

            <FiscalSimulator onCtaClick={scrollToForm} />
          </div>
        </div>
      </section>

      {/* ============================================
          EXEMPLES CONCRETS CUISINE
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Exemples concrets pour votre cuisine
            </h2>
            <p className="text-lg text-muted-foreground">
              Quel que soit votre budget, l'économie est proportionnelle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cuisineExamples.map((example, idx) => {
              const economie = Math.round(example.budget * 0.25);
              const coutReel = example.budget - economie;
              
              return (
                <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden border">
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{example.type}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{example.description}</p>
                    
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Budget</span>
                        <span className="font-semibold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                      </div>
                      
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-green-600">
                          <span>Économie fiscale</span>
                          <span className="font-semibold">-{economie.toLocaleString('fr-CH')} CHF</span>
                        </div>
                        <div className="flex justify-between text-green-700 font-bold mt-1">
                          <span>Coût réel aujourd'hui</span>
                          <span>{coutReel.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                      
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-red-600">
                          <span>Après réforme</span>
                          <span className="font-bold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-orange-100 rounded-lg text-center">
                      <span className="text-orange-800 font-bold">
                        Perte si attente : +{economie.toLocaleString('fr-CH')} CHF
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================
          NOS PRESTATIONS CUISINE
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos prestations</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Une cuisine clé en main,<br />une fiscalité optimisée
              </h2>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Conception et design personnalisé',
                  'Fabrication sur mesure ou grandes marques',
                  'Pose par artisans qualifiés',
                  'Plomberie et électricité incluses',
                  'Accompagnement optimisation fiscale'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>

              <Button 
                size="lg" 
                className="h-14 px-10 rounded-full bg-orange-500 hover:bg-orange-600"
                onClick={scrollToForm}
              >
                Demander un devis cuisine
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE} 
                alt="Cuisine équipée moderne" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-xl shadow-lg">
                <AlertTriangle className="h-8 w-8 mb-2" />
                <p className="font-bold">Fenêtre fiscale</p>
                <p className="text-sm text-white/80">Profitez-en avant 2028</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          TÉMOIGNAGES
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-700">Témoignages</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils ont rénové leur cuisine avant la réforme
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { 
                name: 'Sophie & Marc D.', 
                city: 'Morges', 
                budget: '42\'000 CHF',
                economie: '10\'500 CHF',
                text: 'Notre cuisine de rêve nous a coûté 31\'500 CHF au lieu de 42\'000 CHF. L\'équipe nous a accompagnés sur l\'aspect fiscal dès le départ.'
              },
              { 
                name: 'Famille Rossi', 
                city: 'Genève', 
                budget: '28\'000 CHF',
                economie: '7\'000 CHF',
                text: 'On hésitait à attendre 2026. Heureusement qu\'on a fait les travaux maintenant : 7\'000 CHF d\'économie, c\'est un électroménager complet !'
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold text-lg">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold">{review.name}</p>
                    <p className="text-sm text-muted-foreground">{review.city}</p>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t flex justify-between text-sm">
                  <span className="text-muted-foreground">Budget : {review.budget}</span>
                  <span className="text-green-600 font-semibold">Économie : {review.economie}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          LIEN VERS AUTRES RÉNOVATIONS
          ============================================ */}
      <section className="py-12 bg-gray-50">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-center text-muted-foreground mb-6">Autres projets de rénovation :</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/renovation/salle-de-bain" className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Salle de bain
            </Link>
            <Link to="/renovation/complete" className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Rénovation complète
            </Link>
            <Link to="/renovation" className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Tous nos services rénovation
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          CTA FINAL
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} ref={formRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-orange-500">Devis gratuit cuisine</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Votre cuisine de rêve,<br />au meilleur coût fiscal.
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Décrivez votre projet et recevez une estimation incluant 
                le calcul de votre économie fiscale potentielle.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Estimation économie fiscale incluse',
                  'Devis détaillé sous 48h',
                  'Visite gratuite sur place',
                  'Sans engagement'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <FiscalCountdown />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                  <ChefHat className="h-5 w-5" />
                  Projet cuisine - Optimisation fiscale
                </p>
              </div>
              <ServiceLeadForm service="renovation-cuisine" pole="renovation" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer fiscal */}
      <section className="py-6 bg-gray-100 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center text-muted-foreground">
            Les règles fiscales évoluent suite à la réforme votée en Suisse en 2025.
            Les déductions actuelles restent applicables pendant la phase transitoire.
            Les estimations présentées sont indicatives (taux ~25%) et varient selon votre situation fiscale.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};
